import { makeAutoObservable } from "mobx";
import { store } from "./constant/string";

class UserStore {
  user = false;
  authenticationTOKEN = null;
  expiration = null;
  userId = null;
  isInitialized = false; // Track initialization state

  constructor() {
    makeAutoObservable(this);
    this.initialize();
  }

  initialize() {
    this.loadFromLocalStorage();
    this.isInitialized = true;
  }

  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem(store);
      if (!saved) {
        return;
      }

      const parsed = JSON.parse(saved);

      // Temporary validation before applying state
      const tempExpiration = parsed.expiration
        ? Number(parsed.expiration)
        : null;
      const isTokenValid = tempExpiration && tempExpiration > Date.now();

      if (isTokenValid || !tempExpiration) {
        this.user = parsed.user || false;
        this.authenticationTOKEN = parsed.authenticationTOKEN || null;
        this.expiration = tempExpiration;
        this.userId = parsed.userId || null;

        // Only set timeout if token has expiration
        if (this.expiration) {
          const remainingTime = this.expiration - Date.now();
          if (remainingTime > 2147483647) {
            setTimeout(() => this.logout(), 2147483647);
          } else {
            setTimeout(() => this.logout(), remainingTime);
          }
        }
      } else {
        console.warn("Session expired - cleaning up");
        this.clearSession(); // Clear without removing from storage
      }
    } catch (error) {
      console.error("Failed to load session:", error);
      this.clearSession(); // Clear without removing from storage
    }
  }

  clearSession() {
    // Only clear memory state
    this.user = false;
    this.authenticationTOKEN = null;
    this.expiration = null;
    this.userId = null;
  }

  updateSession({ user, token, expiry, userId }) {
    this.user = user ?? false;
    this.authenticationTOKEN = token ?? null;
    this.setExpiration(expiry);
    this.userId = userId ?? null;
    this.saveToLocalStorage();
  }

  setExpiration(expiry) {
    if (!expiry) {
      this.expiration = null;
      return;
    }

    let expirationTime;

    if (typeof expiry === "string") {
      const fixedDateString = expiry.replace(/(\.\d{6})\d*Z$/, "$1Z");
      expirationTime = new Date(fixedDateString).getTime();

      if (isNaN(expirationTime)) {
        expirationTime = new Date(expiry).getTime();
      }
    } else if (typeof expiry === "number") {
      expirationTime = expiry < 1e12 ? expiry * 1000 : expiry;
    }

    this.expiration = !isNaN(expirationTime) ? expirationTime : null;
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    try {
      const sessionData = {
        user: this.user,
        authenticationTOKEN: this.authenticationTOKEN,
        userId: this.userId,
        expiration: this.expiration,
      };
      localStorage.setItem(store, JSON.stringify(sessionData));
    } catch (error) {
      console.error("Failed to save session:", error);
    }
  }

  logout() {
    this.clearSession();
    try {
      localStorage.removeItem(store);
    } catch (error) {
      console.error("Failed to clear session from storage:", error);
    }
  }

  get isLoggedIn() {
    if (!this.isInitialized) return false;
    return (
      !!this.authenticationTOKEN &&
      (!this.expiration || this.expiration > Date.now())
    );
  }
}

export const userStore = new UserStore();
